// Deterministic mock data generator for the Enrollment Monitoring dashboard.
// Ports the prototype's `derived()` / `bench()` sample-data behavior (see
// dashboard-demo.html) into typed TS. Reacts to filter params the
// same way the prototype does:
//  - `compareTo === 'point-in-time'` hides prior-period deltas (MetricValue.previous/deltaPct = null)
//  - `cohort` changes the section's cohortLabel
//  - `extend` controls which ExtensionMetric entries are returned
import type {
  CriticalElementMonitoringMap,
  ExtendWithOption,
  ExtensionMetric,
  FunnelSection,
  FunnelStageKey,
  FunnelStageMetric,
  MetricValue,
  MonitoringDashboardParams,
  PercentMetricValue,
} from 'containers/user/critical-elements-monitoring/critical-elements-monitoring.types';

const STAGE_ORDER: Array<{ key: FunnelStageKey; label: string }> = [
  { key: 'inquiry', label: 'Inquiries' },
  { key: 'application', label: 'Applications' },
  { key: 'admit', label: 'Admits' },
  { key: 'netDeposit', label: 'Net Deposits' },
];

const DIMENSION_SEEDS = [
  {
    name: 'Out-of-State, Consumer Income $500k+',
    dimensions: [
      {
        ordinal: 0,
        displayName: 'Geographic Region',
        values: ['Out-of-State'],
      },
      { ordinal: 1, displayName: 'Consumer Income', values: ['$500k+'] },
    ],
  },
  {
    name: 'In-State, First Generation',
    dimensions: [
      { ordinal: 0, displayName: 'Geographic Region', values: ['In-State'] },
      { ordinal: 1, displayName: 'First Generation', values: ['Yes'] },
    ],
  },
  {
    name: 'Legacy, Consumer Income $250k-$500k',
    dimensions: [
      { ordinal: 0, displayName: 'Legacy Status', values: ['Legacy'] },
      { ordinal: 1, displayName: 'Consumer Income', values: ['$250k-$500k'] },
    ],
  },
  {
    name: 'International, STEM Intent',
    dimensions: [
      {
        ordinal: 0,
        displayName: 'Geographic Region',
        values: ['International'],
      },
      { ordinal: 1, displayName: 'Intended Major', values: ['STEM'] },
    ],
  },
];

const MOCK_LATENCY_MS = 350;
const TOTAL_FUNNEL_TOP_OF_FUNNEL_INQUIRY_VOLUME = 12000;

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// mulberry32 — small deterministic PRNG so the same seed always yields the same
// sample data (stable across re-renders/tests, unlike Math.random()).
//
// BUG FIX: this hash MUST wrap to a 32-bit int after every accumulation step
// (the `| 0` below), not just `Math.trunc` at the end. `Math.trunc` only
// drops the fractional part — the running `hash * 31 + charCode` sum still
// grows unbounded across a long seed string as a full-precision float, so by
// the time it reaches the bitwise ops in `seededRandom` below, the value has
// already lost the low-order-bit precision `^`/`>>>`'s Int32 coercion needs,
// which silently collapsed every generated number to 0 regardless of the
// seed (verified: two different seeds produced identical output). That
// means every "random" derived value — funnel deltas, comparators, extend
// metrics — was seed-*insensitive*, so changing entering class / cohort /
// which CE row appeared to do nothing. `| 0` forces the correct 32-bit
// wraparound each iteration, matching the standard string-hash idiom.
// Linters may suggest `Math.trunc` in place of `| 0` here (e.g. Sonar
// S7767) — that suggestion is incorrect for this function and reintroduces
// the bug described above: `Math.trunc` only drops the fractional part,
// while `| 0` performs the actual ToInt32 32-bit wraparound this hash
// depends on. Do not "fix" this to `Math.trunc`.
const hashSeed = (seed: string): number =>
  Array.from(seed).reduce(
    (hash, char) => (hash * 31 + (char.codePointAt(0) ?? 0)) | 0,
    0x9e3779b9
  );

const seededRandom = (seed: string): (() => number) => {
  let a = hashSeed(seed);

  return () => {
    // Same `| 0` requirement as hashSeed above — needed for 32-bit
    // wraparound, not `Math.trunc`.
    a = (a + 0x6d2b79f5) | 0;

    let t = Math.imul(a ^ (a >>> 15), 1 | a);

    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;

    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const round1 = (value: number): number => Math.round(value * 10) / 10;

const buildMetricValue = (
  rng: () => number,
  current: number,
  compareTo: MonitoringDashboardParams['compareTo']
): MetricValue => {
  if (compareTo === 'point-in-time') {
    return { current, previous: null, deltaPct: null };
  }

  const previous = Math.round(current * (0.85 + rng() * 0.25));
  const deltaPct =
    previous === 0 ? null : round1(((current - previous) / previous) * 100);

  return { current, previous, deltaPct };
};

const buildPercentMetricValue = (
  rng: () => number,
  current: number,
  compareTo: MonitoringDashboardParams['compareTo']
): PercentMetricValue => {
  if (compareTo === 'point-in-time') {
    return {
      current: round1(current),
      previous: null,
      deltaPct: null,
      pp: null,
    };
  }

  const rawPrevious = current * (0.85 + rng() * 0.25);
  const deltaPct =
    rawPrevious === 0
      ? null
      : round1(((current - rawPrevious) / rawPrevious) * 100);

  return {
    current: round1(current),
    previous: round1(rawPrevious),
    deltaPct,
    pp: round1(current - rawPrevious),
  };
};

const buildComparators = (
  rng: () => number,
  params: Pick<MonitoringDashboardParams, 'show'>
): FunnelStageMetric['comparators'] => {
  const comparators: FunnelStageMetric['comparators'] = {};

  const rawClient = params.show.client ? rng() * 8 - 4 : undefined;
  const rawCohort = params.show.cohort ? rng() * 8 - 4 : undefined;

  if (rawClient !== undefined) comparators.client = round1(rawClient);
  if (rawCohort !== undefined) comparators.cohort = round1(rawCohort);
  // Mirrors dashboard-demo.html's bench(): "My School vs Cohort" isn't an
  // independent value — it's the delta between the other two rows, and
  // only shown when both of them are (`state.yields.vsCohort &&
  // state.yields.client && state.yields.cohort`).
  if (
    params.show.vsCohort &&
    rawClient !== undefined &&
    rawCohort !== undefined
  ) {
    comparators.vsCohort = round1(rawClient - rawCohort);
  }

  const rawClientPp = params.show.client ? rng() * 8 - 4 : undefined;
  const rawCohortPp = params.show.cohort ? rng() * 8 - 4 : undefined;

  if (rawClientPp !== undefined) comparators.clientPp = round1(rawClientPp);
  if (rawCohortPp !== undefined) comparators.cohortPp = round1(rawCohortPp);
  if (
    params.show.vsCohort &&
    rawClientPp !== undefined &&
    rawCohortPp !== undefined
  ) {
    comparators.vsCohortPp = round1(rawClientPp - rawCohortPp);
  }

  const rawClientProportion = params.show.client ? rng() * 8 - 4 : undefined;
  const rawCohortProportion = params.show.cohort ? rng() * 8 - 4 : undefined;

  if (rawClientProportion !== undefined) {
    comparators.clientProportion = round1(rawClientProportion);
  }
  if (rawCohortProportion !== undefined) {
    comparators.cohortProportion = round1(rawCohortProportion);
  }
  if (
    params.show.vsCohort &&
    rawClientProportion !== undefined &&
    rawCohortProportion !== undefined
  ) {
    comparators.vsCohortProportion = round1(
      rawClientProportion - rawCohortProportion
    );
  }

  return comparators;
};

// Shared by funnel stages and Extend With cards — both expose the identical
// volume/rate/proportion/comparators shape and must react to compareTo/show
// the same way. Extracting this stops the two card types' data-generation
// logic from drifting the way their rendering logic previously did (see
// extension-card.tsx).
const buildMetricTriple = (
  rng: () => number,
  params: Pick<MonitoringDashboardParams, 'compareTo' | 'show'>,
  volumeCurrent: number,
  rateCurrent: number,
  proportionCurrent: number
): Pick<
  FunnelStageMetric,
  'volume' | 'rate' | 'proportion' | 'comparators'
> => ({
  volume: buildMetricValue(rng, volumeCurrent, params.compareTo),
  rate: buildPercentMetricValue(rng, rateCurrent, params.compareTo),
  proportion: buildPercentMetricValue(rng, proportionCurrent, params.compareTo),
  comparators: buildComparators(rng, params),
});

const buildStages = (
  rng: () => number,
  params: Pick<MonitoringDashboardParams, 'compareTo' | 'show'>,
  topOfFunnelVolume: number
): FunnelStageMetric[] => {
  const volumes: number[] = [];
  let current = topOfFunnelVolume;

  STAGE_ORDER.forEach((_, index) => {
    volumes.push(Math.round(current));
    current *= 0.35 + rng() * 0.25 + (index === 0 ? 0.35 : 0);
  });

  return STAGE_ORDER.map((stage, index) => {
    const volume = volumes[index];
    const priorStage = index === 0 ? topOfFunnelVolume : volumes[index - 1];
    const rateCurrent = index === 0 ? 100 : (volume / priorStage) * 100;
    const proportionCurrent = (volume / topOfFunnelVolume) * 100;

    return {
      stageKey: stage.key,
      ...buildMetricTriple(rng, params, volume, rateCurrent, proportionCurrent),
    };
  });
};

const EXTENSION_META: Record<
  ExtendWithOption,
  { label: string; secondaryCaption: string }
> = {
  prospect: {
    label: 'Prospects',
    secondaryCaption: 'Historical inquiry conversion: 18.2%',
  },
  'financial-aid': {
    label: 'Filers',
    secondaryCaption: 'Historical persistence: 87.4%',
  },
  retention: {
    label: 'Retention',
    secondaryCaption: 'Historical re-enrollment: 91.6%',
  },
};

// ExtensionMetric shares the same volume/rate/proportion/comparators shape as
// a funnel stage, but the spec doesn't define what an Extend With card's
// "rate" represents (there's no natural prior-stage-to-this-stage conversion
// the way there is for a real funnel stage). Assumption, flagged rather than
// silently picked: `rate` is modeled as a plausible generic conversion-rate
// figure (30-70%) for that Extend With pool, independent of `volume`.
// `proportion` DOES have a defined convention per the contract ("% of
// top-of-funnel inquiries, same convention as stages"), so that one mirrors
// the stage formula exactly.
const buildExtensions = (
  rng: () => number,
  params: Pick<MonitoringDashboardParams, 'compareTo' | 'show' | 'extend'>,
  topOfFunnelVolume: number
): ExtensionMetric[] =>
  params.extend.map((type) => {
    // Scaled off `topOfFunnelVolume` (5%-45% of it) rather than a fixed
    // 500-4500 range — the fixed range produced a >100% "share of top-of-
    // funnel inquiries" for small per-CE funnels (a CE's own inquiry count
    // can be in the low hundreds), which isn't a plausible proportion value.
    const volumeCurrent = Math.round(topOfFunnelVolume * (0.05 + rng() * 0.4));
    const rateCurrent = 30 + rng() * 40;
    const proportionCurrent = (volumeCurrent / topOfFunnelVolume) * 100;
    const meta = EXTENSION_META[type];

    return {
      type,
      label: meta.label,
      secondaryCaption: meta.secondaryCaption,
      ...buildMetricTriple(
        rng,
        params,
        volumeCurrent,
        rateCurrent,
        proportionCurrent
      ),
    };
  });

const buildFunnelSection = (
  seed: string,
  params: Pick<
    MonitoringDashboardParams,
    'enteringClass' | 'compareTo' | 'cohort' | 'extend' | 'show'
  >,
  topOfFunnelVolume: number
): FunnelSection => {
  const rng = seededRandom(seed);

  return {
    period: {
      monitoringYear: params.enteringClass,
      enteringClass: params.enteringClass,
    },
    cohortLabel: params.cohort,
    stages: buildStages(rng, params, topOfFunnelVolume),
    extensions: buildExtensions(rng, params, topOfFunnelVolume),
  };
};

// TEMPORARY MOCK: entering classes still come from this mock generator —
// only the cohort list has a real endpoint so far (see
// hooks/use-monitoring-filter-options.ts, which now sources cohorts from
// `useGetClientCohorts`).
export const fetchMonitoringEnteringClasses = async (): Promise<number[]> => {
  await delay(MOCK_LATENCY_MS);

  return [2027, 2026];
};

const getCriticalElementSeedInquiryVolume = (
  clientId: string,
  index: number,
  seedName: string
): number => {
  const rng = seededRandom(`${clientId}-ce-${index}-${seedName}`);

  return Math.round(400 + rng() * 1600);
};

export const fetchMonitoringDashboardMetrics = async (
  params: MonitoringDashboardParams
): Promise<CriticalElementMonitoringMap> => {
  await delay(MOCK_LATENCY_MS);

  const total = buildFunnelSection(
    `${params.clientId}-total-${params.enteringClass}-${params.cohort}`,
    params,
    TOTAL_FUNNEL_TOP_OF_FUNNEL_INQUIRY_VOLUME
  );

  const byCriticalPopulationId: Record<string, FunnelSection> = {};

  DIMENSION_SEEDS.forEach((seed, index) => {
    const id = `ce-${index + 1}`;
    const inquiryVolume = getCriticalElementSeedInquiryVolume(
      params.clientId,
      index,
      seed.name
    );

    byCriticalPopulationId[id] = buildFunnelSection(
      `${params.clientId}-metrics-${id}-${index}-${params.enteringClass}-${params.cohort}`,
      params,
      inquiryVolume
    );
  });

  return { total, byCriticalPopulationId };
};
